/**
 * IframeHeightResizer
 *
 * Module to manage dynamic iframe height in the Winalist widget.
 * Calculates content height and sends a postMessage to the parent.
 */
class IframeHeightResizer {
  constructor() {
    this.lastHeight = 0;
    this.lastSentHeight = 0;
    this.resizeObserver = null;
    this.modalResizeObserver = null;
    this.debounceTimeout = null;
    this.cooldownTimeout = null;
    this.settleIntervalId = null;
    this.settleTimeoutId = null;
    this.safetyIntervalId = null;
    this.lastSendTime = 0;
    this.pendingHeight = null;
    this.isMeasuring = false;
    this.rafId = null;
    this.isScrolling = false;
    this.scrollTimeout = null;
    this.lastSentModalActive = null;
    this.lastSentCheckoutFillsModalMaxHeight = null;

    // Debounce delay - wait for content to stabilize before sending
    this.DEBOUNCE_DELAY = 150; // ms - slightly increased for better stability
    this.MIN_HEIGHT_CHANGE = 10; // px - minimum change to trigger update
    this.SEND_COOLDOWN = 480; // ms - aligned with parent iframe max-height transition (~450ms)
    this.SETTLE_INTERVAL = 120; // ms - increased to reduce polling frequency
    this.SETTLE_STABLE_PX = 2; // px
    // Longer stability window reduces multiple sends while async content still settles (e.g. payment step).
    this.SETTLE_REQUIRED_STABLE_TICKS = 4; // 4 * 120ms = 480ms stable
    this.SETTLE_MAX_DURATION = 900; // ms
    this.SCROLL_DEBOUNCE = 150; // ms - debounce during scroll
    this.DEFAULT_HEIGHT_BUFFER = 4; // px - absorb fractional layout rounding
    this.RETINA_HEIGHT_BUFFER = 8; // px - extra safety for DPR>=2 / Safari rounding

    // Parent animates max-height (~450ms). During that window, viewport changes inside the iframe
    // would otherwise cause our ResizeObserver to send new heights, which restarts/jitters the animation.
    // Suppress resends inside this window unless the height changes significantly or modal state toggles.
    this.POST_SEND_TRANSITION_WINDOW = 500; // ms
    this.IN_TRANSITION_FORCE_SEND_PX = 40; // px - only re-send mid-animation for big changes

    // Check if we are inside an iframe
    if (window.self !== window.top) {
      this.init();
    }
  }

  /**
   * Measures an element's content height while temporarily ignoring inline min-height in vh.
   * This prevents feedback loops where iframe resizing changes vh-based min-heights,
   * which in turn changes the measured height (even when DOM does not change).
   */
  measureHeightIgnoringVhMinHeight(element) {
    if (!element) return 0;

    const inlineMinHeight = element.style && element.style.minHeight ? element.style.minHeight : '';
    const hasInlineVhMinHeight = typeof inlineMinHeight === 'string' && inlineMinHeight.indexOf('vh') !== -1;

    if (!hasInlineVhMinHeight) {
      return element.scrollHeight || element.offsetHeight || 0;
    }

    // Temporarily disable min-height for measurement
    const prevMinHeight = element.style.minHeight;
    const prevMaxHeight = element.style.maxHeight;
    const prevHeight = element.style.height;

    this.isMeasuring = true;
    try {
      element.style.minHeight = '0px';
      element.style.maxHeight = 'none';
      element.style.height = 'auto';

      // Force layout
      // eslint-disable-next-line no-unused-expressions
      element.offsetHeight;

      return element.scrollHeight || element.offsetHeight || 0;
    } finally {
      element.style.minHeight = prevMinHeight;
      element.style.maxHeight = prevMaxHeight;
      element.style.height = prevHeight;
      this.isMeasuring = false;
    }
  }

  /**
   * Calculates actual content height by measuring children
   * This avoids inflated heights from min-height CSS rules
   */
  getActualContentHeight(element) {
    if (!element) return 0;

    let totalHeight = 0;
    const children = element.children;

    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      const style = window.getComputedStyle(child);

      // Skip hidden elements
      if (style.display === 'none' || style.visibility === 'hidden') continue;

      // If child uses inline min-height in vh (e.g. style="min-height: 80vh"),
      // measure its content height ignoring that min-height to avoid resize loops.
      const measuredChildHeight = this.measureHeightIgnoringVhMinHeight(child);

      // Get element height including margins
      const marginTop = parseInt(style.marginTop) || 0;
      const marginBottom = parseInt(style.marginBottom) || 0;
      totalHeight += measuredChildHeight + marginTop + marginBottom;
    }

    // Add padding of parent element
    const parentStyle = window.getComputedStyle(element);
    const paddingTop = parseInt(parentStyle.paddingTop) || 0;
    const paddingBottom = parseInt(parentStyle.paddingBottom) || 0;

    return totalHeight + paddingTop + paddingBottom;
  }

  /**
   * Gets the full content height
   * When a modal is active, calculate actual content height to avoid min-height inflation
   */
  getContentHeight() {
    const body = document.body;

    // Check if a modal is active - get all active modals and use the last one (innermost/topmost)
    const activeModals = document.querySelectorAll('.modal.active');

    if (activeModals.length > 0) {
      // Use the last active modal (typically the topmost/innermost one like pick_extras_modal)
      const activeModal = activeModals[activeModals.length - 1];
      const modalContainer = activeModal.querySelector('.modal-container');
      const modalContent = activeModal.querySelector('.modal-content');

      // Try to get actual content height (not inflated by min-height CSS)
      let height = 0;

      if (modalContent) {
        // Calculate actual height from children to avoid min-height: 80vh
        height = this.getActualContentHeight(modalContent);
      }

      // Fallback to container if content height is too small
      if (height < 100 && modalContainer) {
        height = this.getActualContentHeight(modalContainer);
      }

      // Final fallback to scrollHeight
      if (height < 100) {
        height = modalContainer ? modalContainer.scrollHeight : activeModal.scrollHeight;
      }

      // Add some padding for modal chrome (header, close button, etc.)
      return Math.max(height + 50, 100);
    }

    // No modal active: keep the original simple behavior that was stable on Safari,
    // then rely on the bottom buffer to absorb Retina rounding.
    return Math.max(body.scrollHeight, 100);
  }

  /**
   * Small bottom buffer to absorb fractional pixel rounding that can otherwise
   * leave a 1-6px scrollbar visible, especially on Retina / Safari.
   */
  getHeightBuffer() {
    return window.devicePixelRatio >= 2 ? this.RETINA_HEIGHT_BUFFER : this.DEFAULT_HEIGHT_BUFFER;
  }

  /**
   * Matches embed.js modal detection so parent iframe max-height stays in sync with UI state.
   */
  isModalActive() {
    if (document.body.classList.contains('modal-active')) {
      return true;
    }

    return document.querySelector('.modal.active') !== null;
  }

  /**
   * Stripe payment (#checkouta) or free booking form — parent can match iframe height to modal max (vh).
   */
  isCheckoutPaymentOrFreeStep() {
    return document.getElementById('checkouta') !== null
      || document.getElementById('free-booking-form') !== null;
  }

  /**
   * Request a height update - debounced to only fire once after stabilization
   * Uses requestAnimationFrame to avoid blocking scroll
   */
  requestHeightUpdate() {
    // Skip updates during active scrolling to prevent jank
    if (this.isScrolling) return;

    // Clear any pending update
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    // Cancel any pending RAF
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    const debounceDelay = document.getElementById('checkouta') ? 280 : this.DEBOUNCE_DELAY;

    // Schedule update after debounce delay using RAF for smooth performance
    this.debounceTimeout = setTimeout(() => {
      this.debounceTimeout = null;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.settleAndSendHeight();
      });
    }, debounceDelay);
  }

  /**
   * Waits for layout to stabilize, then sends ONE final height.
   * This prevents the "3-4-5 mini-resizes" effect while content is still settling.
   */
  settleAndSendHeight() {
    // Stop any previous settle pass
    if (this.settleIntervalId) {
      clearInterval(this.settleIntervalId);
      this.settleIntervalId = null;
    }
    if (this.settleTimeoutId) {
      clearTimeout(this.settleTimeoutId);
      this.settleTimeoutId = null;
    }

    let last = this.getContentHeight();
    let stableTicks = 0;

    const finish = (finalHeight) => {
      if (this.settleIntervalId) clearInterval(this.settleIntervalId);
      if (this.settleTimeoutId) clearTimeout(this.settleTimeoutId);
      this.settleIntervalId = null;
      this.settleTimeoutId = null;
      this.queueOrSendHeight(finalHeight);
    };

    // Hard stop to avoid running forever
    this.settleTimeoutId = setTimeout(() => finish(last), this.SETTLE_MAX_DURATION);

    this.settleIntervalId = setInterval(() => {
      const current = this.getContentHeight();
      if (Math.abs(current - last) <= this.SETTLE_STABLE_PX) {
        stableTicks += 1;
      } else {
        stableTicks = 0;
        last = current;
      }

      if (stableTicks >= this.SETTLE_REQUIRED_STABLE_TICKS) {
        finish(current);
      }
    }, this.SETTLE_INTERVAL);
  }

  /**
   * Applies send cooldown to avoid back-to-back postMessage spam.
   * If a send is attempted during cooldown, keep only the latest height.
   */
  queueOrSendHeight(height) {
    if (typeof height !== 'number' || isNaN(height) || height <= 0) return;

    const now = Date.now();
    const remaining = this.SEND_COOLDOWN - (now - this.lastSendTime);

    if (remaining > 0) {
      // Store latest height and schedule a single send at the end of cooldown
      this.pendingHeight = height;

      if (this.cooldownTimeout) {
        clearTimeout(this.cooldownTimeout);
      }

      this.cooldownTimeout = setTimeout(() => {
        this.cooldownTimeout = null;
        const pending = this.pendingHeight;
        this.pendingHeight = null;
        if (pending !== null) {
          this.sendHeightToParent(pending);
        }
      }, remaining);

      return;
    }

    this.sendHeightToParent(height);
  }

  /**
   * Sends height to parent via postMessage (called after settle/cooldown)
   */
  sendHeightToParent(forcedHeight = null) {
    const measuredHeight = forcedHeight !== null ? forcedHeight : this.getContentHeight();
    const height = Math.ceil(measuredHeight + this.getHeightBuffer());
    const modalActive = this.isModalActive();
    const checkoutFillsModalMaxHeight = this.isCheckoutPaymentOrFreeStep();

    // Only send if height changed significantly from last sent value, unless modal open/close toggled
    const heightDiff = Math.abs(height - this.lastSentHeight);
    const modalStateChanged = modalActive !== this.lastSentModalActive;
    const checkoutMaxChanged = checkoutFillsModalMaxHeight !== this.lastSentCheckoutFillsModalMaxHeight;
    if (height <= 0) {
      return;
    }

    if (heightDiff < this.MIN_HEIGHT_CHANGE && !modalStateChanged && !checkoutMaxChanged) {
      return;
    }

    // While the parent iframe is animating (max-height transition), suppress minor height updates
    // triggered by the iframe's own viewport changes to avoid restarting/jittering the animation.
    const sinceLastSend = Date.now() - this.lastSendTime;
    const inTransitionWindow = this.lastSendTime > 0 && sinceLastSend < this.POST_SEND_TRANSITION_WINDOW;
    if (inTransitionWindow && !modalStateChanged && !checkoutMaxChanged && heightDiff < this.IN_TRANSITION_FORCE_SEND_PX) {
      return;
    }

    this.lastSentHeight = height;
    this.lastSentModalActive = modalActive;
    this.lastSentCheckoutFillsModalMaxHeight = checkoutFillsModalMaxHeight;
    this.lastSendTime = Date.now();

    // Send message to parent
    try {
      window.parent.postMessage({
        type: 'winalist-iframe-resize',
        height: height,
        modalActive: modalActive,
        checkoutFillsModalMaxHeight: checkoutFillsModalMaxHeight
      }, '*');
    } catch (error) {
      console.error('Error sending height to parent:', error);
    }
  }

  /**
   * Observes active modals for size changes
   */
  observeActiveModals() {
    if (!this.modalResizeObserver) return;

    // Disconnect previous observations
    this.modalResizeObserver.disconnect();

    // Find and observe active modals and their content
    const activeModals = document.querySelectorAll('.modal.active');
    activeModals.forEach((modal) => {
      this.modalResizeObserver.observe(modal);

      const modalContainer = modal.querySelector('.modal-container');
      const modalContent = modal.querySelector('.modal-content');

      if (modalContainer) this.modalResizeObserver.observe(modalContainer);
      if (modalContent) this.modalResizeObserver.observe(modalContent);
    });

    // Also observe the pick extras modal specifically (even if not yet active)
    // This handles dynamically loaded modals that weren't present at page load
    const pickExtrasModal = document.getElementById('pickExtras');
    if (pickExtrasModal) {
      this.modalResizeObserver.observe(pickExtrasModal);

      const extrasContainer = pickExtrasModal.querySelector('.modal-container');
      const extrasContent = pickExtrasModal.querySelector('.modal-content');

      if (extrasContainer) this.modalResizeObserver.observe(extrasContainer);
      if (extrasContent) this.modalResizeObserver.observe(extrasContent);
    }
  }

  /**
   * Initializes the module
   */
  init() {
    const bodyElement = document.body;

    // Track scroll state to pause height updates during scroll (prevents jank on mobile)
    const handleScrollStart = () => {
      this.isScrolling = true;
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      this.scrollTimeout = setTimeout(() => {
        this.isScrolling = false;
        // After scroll ends, do one final height check
        this.requestHeightUpdate();
      }, this.SCROLL_DEBOUNCE);
    };

    // Use passive listeners for scroll/touch to prevent blocking
    window.addEventListener('scroll', handleScrollStart, { passive: true });
    window.addEventListener('touchmove', handleScrollStart, { passive: true });

    // Send initial height after complete loading
    if (document.readyState === 'complete') {
      // Small delay to ensure everything is rendered
      setTimeout(() => this.sendHeightToParent(), 100);
    } else {
      window.addEventListener('load', () => {
        setTimeout(() => this.sendHeightToParent(), 100);
      });
    }

    // Observe <body> element height changes using ResizeObserver
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.requestHeightUpdate();
      });
      this.resizeObserver.observe(bodyElement);

      // Separate observer for modal elements
      this.modalResizeObserver = new ResizeObserver(() => {
        this.requestHeightUpdate();
      });
    }

    // Observe DOM changes that might affect height
    // IMPORTANT: Only observe direct children and class changes to reduce overhead
    if (window.MutationObserver) {
      const mutationObserver = new MutationObserver((mutations) => {
        // Ignore mutations caused by our own temporary measurement style tweaks
        if (this.isMeasuring) return;

        // Skip during scroll to prevent jank
        if (this.isScrolling) return;

        // Check if any mutation is relevant (modal class changes or significant DOM changes)
        let shouldUpdate = false;
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            // Only care about modal-related class changes
            const target = mutation.target;
            if (target.classList && (
              target.classList.contains('modal') ||
              target.classList.contains('modal-active') ||
              target === bodyElement
            )) {
              shouldUpdate = true;
              break;
            }
          }
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // Only care about significant DOM additions
            for (const node of mutation.addedNodes) {
              if (node.nodeType === Node.ELEMENT_NODE) {
                shouldUpdate = true;
                break;
              }
            }
            if (shouldUpdate) break;
          }
        }
        if (shouldUpdate) {
          this.observeActiveModals();
          this.requestHeightUpdate();
        }
      });

      // Reduced scope: only watch body's direct children and class attribute
      mutationObserver.observe(bodyElement, {
        childList: true,
        subtree: false, // Changed from true - only watch direct children
        attributes: true,
        attributeFilter: ['class'] // Removed 'style' - less frequent updates
      });

      // Also observe modal containers specifically (more targeted)
      const modalContainers = document.querySelectorAll('.modal');
      modalContainers.forEach(modal => {
        mutationObserver.observe(modal, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class']
        });
      });
    }

    // Listen to navigation events (for AJAX modals)
    window.addEventListener('popstate', () => {
      this.requestHeightUpdate();
    }, { passive: true });

    // Listen to modal events
    document.addEventListener('modal-opened', () => {
      this.observeActiveModals();
      this.requestHeightUpdate();
    }, { passive: true });

    document.addEventListener('modal-closed', () => {
      this.observeActiveModals();
      this.requestHeightUpdate();
    }, { passive: true });

    // Safety fallback - check periodically (increased interval)
    this.safetyIntervalId = setInterval(() => {
      if (!this.isScrolling) {
        this.requestHeightUpdate();
      }
    }, 15000); // Increased from 10s to 15s
  }

  /**
   * Cleans up observers and listeners
   */
  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.modalResizeObserver) {
      this.modalResizeObserver.disconnect();
    }

    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    if (this.cooldownTimeout) {
      clearTimeout(this.cooldownTimeout);
    }

    if (this.settleIntervalId) {
      clearInterval(this.settleIntervalId);
    }

    if (this.settleTimeoutId) {
      clearTimeout(this.settleTimeoutId);
    }

    if (this.safetyIntervalId) {
      clearInterval(this.safetyIntervalId);
    }

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }
}

export default IframeHeightResizer;
